import { useEffect, useMemo, useState } from 'react'
import Explorer, { loadExample } from './Explorer'
import { type FRXExample, type FRXIndex } from '../../utils/api'

const DRAFT_KEY = 'frv.playground.v1'

interface Draft { template: string; prog: string }

function readDraft(): Draft | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY)
    return raw ? (JSON.parse(raw) as Draft) : null
  } catch { return null }
}

function writeDraft(d: Draft) {
  try { localStorage.setItem(DRAFT_KEY, JSON.stringify(d)) } catch { /* private mode: drafts are a convenience */ }
}

/** QParam constructs, as FormalRV's language reference (Exercise/LANGUAGE.md §1) defines them, and the
 *  examples whose compiled source uses each one. */
const REFERENCE: { form: string; means: string; seen: string[] }[] = [
  { form: 'code surface d=3', means: 'the code every logical qubit is stored in, and its distance (change d to compile at another)', seen: ['pauli'] },
  { form: 'params n, a', means: 'value parameters of the program, fixed at compile time', seen: ['pauli', 'pauli-cond'] },
  { form: 'program[q: W]:', means: 'the program body over W qubit wires', seen: ['pauli'] },
  { form: 'program name[q: W, c: K]:', means: 'a named program that also declares K measurement records', seen: ['dj', 'grover'] },
  { form: 'X(q) · H(q) · Z(q)', means: 'single-qubit gates on wire q', seen: ['pauli', 'dj'] },
  { form: 'Rz(q, k)', means: 'phase exp(−iπ/2^k) on |1⟩; k is the depth', seen: ['dj'] },
  { form: 'CX(a, b) · CZ(a, b)', means: 'controlled-X and controlled-Z', seen: ['dj', 'grover'] },
  { form: 'CCX(a, b, c)', means: 'Toffoli: needs a magic state once compiled', seen: ['dj', 'grover'] },
  { form: 'measure q ↦ c[k]', means: 'measure wire q into record c[k] (ASCII -> also works)', seen: ['dj', 'grover'] },
  { form: 'for lo <= v < hi:', means: 'a loop; both bounds explicit; never unrolled by the compilers', seen: ['pauli-loop'] },
  { form: 'if (a >> i) % 2 = 1: … else:', means: 'compile-time branch on a bit of a parameter; the condition must be such a bit test', seen: ['pauli-cond', 'grover'] },
  { form: 'ctrl q:', means: 'quantum control of a block', seen: ['dj'] },
  { form: 'when c[k]:', means: 'run-time branch on a measurement record', seen: ['dj', 'grover'] },
  { form: 'gadget g[q: W](a, b):', means: 'a reusable gadget with declared width and parameters', seen: ['pauli-call'] },
  { form: 'g(a, b) @ id()', means: 'call g; the placement map after @ is mandatory', seen: ['pauli-call'] },
  { form: 'map id()(i) = i', means: 'a wire map from the callee’s wires to the caller’s', seen: ['pauli-call'] },
  { form: 'define f(x) = …', means: 'a function for specifications only: not in program bodies or wire indices', seen: ['pauli-loop'] },
  { form: 'use ../Lib/lib.prog', means: 'import the gadgets of another file', seen: ['dj'] },
  { form: 'spec g: … requires / assumes / ensures / outcomes / resource', means: 'the specification Lean proves; optional for compiling', seen: ['pauli'] },
]

export default function Playground({ index, picker }: { index: FRXIndex; picker?: React.ReactNode }) {
  const saved = useMemo(readDraft, [])
  const templateId = saved?.template && index.examples.some(e => e.id === saved.template) ? saved.template : 'pauli'
  const [template, setTemplate] = useState<FRXExample | null>(null)
  const [syntax, setSyntax] = useState(false)

  useEffect(() => { loadExample(templateId).then(setTemplate).catch(() => setTemplate(null)) }, [templateId])

  const example = useMemo<FRXExample | null>(() => {
    if (!template) return null
    return {
      ...template,
      id: 'playground', unit: 'Main', title: 'Your program', introduces: '', category: 'playground',
      steps: [], passes: [], vm: null, variants: [], lean: [],
      texts: { prog: saved?.prog || template.texts.prog, sysprog: '' },
      machine: '', machine_path: '',
    }
  }, [template, saved])

  return (
    <>
      {example && (
        <Explorer
          key="playground"
          exampleId="playground"
          example={example}
          playground
          initialPane="qppm"
          onSourceChange={text => writeDraft({ template: templateId, prog: text })}
          toolbar={(
            <>
              {picker}
              <button onClick={() => setSyntax(!syntax)}
                className={`h-7 px-2.5 rounded-md text-xs font-medium ${syntax ? 'bg-qcloud-primary/10 text-qcloud-primary' : 'text-qcloud-muted hover:text-qcloud-text'}`}>
                Syntax
              </button>
            </>
          )}
        />
      )}

      {syntax && (
        <div className="absolute right-3 top-12 bottom-12 w-[min(560px,92%)] z-10 overflow-auto rounded-lg border border-qcloud-border bg-white shadow-xl">
          <div className="sticky top-0 flex items-center justify-between px-4 py-2 bg-white border-b border-qcloud-border">
            <span className="text-sm font-semibold text-qcloud-text">QParam syntax</span>
            <button onClick={() => setSyntax(false)} aria-label="Close" className="text-qcloud-muted hover:text-qcloud-text px-1">×</button>
          </div>
          <table className="w-full text-sm table-fixed">
            <tbody>
              {REFERENCE.map(r => (
                <tr key={r.form} className="border-t border-slate-100 align-top first:border-t-0">
                  <td className="w-[45%] py-1.5 pl-4 pr-3 font-mono text-[12px] text-qcloud-text break-words">{r.form}</td>
                  <td className="py-1.5 pr-4 text-[12.5px] text-qcloud-muted">{r.means}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
